import React, { useContext, useEffect, useRef, useState } from 'react'

import { WappContext } from 'wapplr-react/dist/common/Wapp'

import Button from 'karrierhub-ui/dist/common/src/components/Button'
import Modal from 'karrierhub-ui/dist/common/src/components/Modal'
import Typography from 'karrierhub-ui/dist/common/src/components/Typography'

import DefaultSubmit from '../Form/Components/DefaultSubmit'

import clsx from 'clsx'

import AppContext from '../App/context'

import style from './style.css'

export default function Dialog(props) {

    const context = useContext(WappContext)
    const appContext = useContext(AppContext)

    const { wapp } = context

    wapp.styles.use(style)

    const {
        ModalComponent = Modal,
        cancelText = '',
        CancelProps = {},
        submitText = '',
        SubmitProps = {},
        dialogTitle = '',
        dialogContent = '',
        className,
        PaperComponentProps = {},
        ContentComponent = Typography,
        ContentComponentProps = {},
        BackdropComponentProps = {},
        size = 'large',
        showLeaveWarning = false,
        ...rest
    } = props

    const form = useRef()
    const modal = useRef()
    const scrollElement = useRef()
    const innerDialog = useRef()

    const open = useRef(false)

    async function setOpen(value) {
        if (open.current !== value) {
            open.current = value
            const _open = modal.actions.getOpen()
            if (_open !== value) {
                await modal.actions.setOpen(value)
            }
        }
    }

    const [show, setShow] = useState(true)
    const [dialogProps, setDialogProps] = useState({
        cancelText,
        CancelProps,
        submitText,
        SubmitProps,
        dialogTitle,
        dialogContent
    })

    const Form = dialogProps.Form

    const formSubmit = async function(e, formData) {
        return await handleSubmit(e, formData)
    }

    const handleOpen = async (dialogProps) => {
        if (dialogProps) {
            await setDialogProps(dialogProps)
        }
        await setOpen(true)
    }

    const handleClose = async () => {
        await setOpen(false)
    }

    const handleCancel = async (e) => {
        if (dialogProps.onCancelBeforeClose) {
            await dialogProps.onCancelBeforeClose(e)
        }
        await setOpen(false)
        if (dialogProps.onCancel) {
            await dialogProps.onCancel(e)
        }
    }

    const handleSubmit = async (e, formData) => {
        if (!formData && form.current) {
            if (e) {
                e.preventDefault()
            }
            return await form.current.onSubmit(e)
        }
        if (e) {
            e.preventDefault()
        }
        if (dialogProps.onSubmit) {

            const response = await dialogProps.onSubmit(e, formData)

            if ((response && response.error) || (response && response.errors)) {

                if (form.current) {
                    return response
                } else {
                    await setOpen(false)
                }

                const message = response.error?.message || response.errors?.['0']?.message

                if (message) {
                    await appContext.template.current?.actions?.setSnackMessage(message, 6000, 'error')
                }

            } else if (response) {
                if (dialogProps.successMessage) {
                    await appContext.template.current?.actions?.setSnackMessage(dialogProps.successMessage)
                }
                await setOpen(false)
            }

        } else {
            await setOpen(false)
        }
    }

    const handleShow = async () => {
        if (!show) {
            await setShow(true)
        }
    }

    const handleHide = async () => {
        if (show) {
            await setShow(false)
        }
    }

    useEffect(function() {

        const actions = {
            open: handleOpen,
            close: handleClose,
            cancel: handleCancel,
            submit: handleSubmit,
            show: handleShow,
            hide: handleHide,
            getScrollElement: () => {
                return scrollElement.current?.parentElement
            }
        }

        if (props.effect) {
            props.effect({
                actions
            })
        }

    })

    const submitRef = useRef()

    const SubmitButtonComponent = props.SubmitButtonComponent ? props.SubmitButtonComponent : Form ? DefaultSubmit : Button

    return (
        <ModalComponent
            className={clsx(
                style.dialog,
                {
                    [style[className]]: className && style[className],
                    [className]: className && !style[className],
                    [style.hidden]: !show
                }
            )}
            {...rest}
            effect={({ actions }) => {
                modal.actions = actions
            }}
            open={open.current}
            {...showLeaveWarning ? {
                beforeClose: async () => {

                    const shouldLeaveWarning = appContext.getShouldLeaveWarning()

                    if (shouldLeaveWarning && innerDialog.actions) {

                        let preventClose = false

                        await new Promise((resolve) => {

                            innerDialog.actions.open({
                                dialogTitle: appContext.titles.leaveWarningTitle,
                                dialogContent: appContext.messages.leaveWarningMessage,
                                cancelText: appContext.labels.leaveWarningCancel,
                                submitText: appContext.labels.leaveWarningSubmit,
                                onSubmit: async function() {

                                    appContext.setShouldLeaveWarning(false)
                                    innerDialog.actions.close()

                                    preventClose = false

                                    resolve()

                                },
                                onCancel: () => {
                                    preventClose = true
                                    resolve()
                                }
                            })

                        })

                        return preventClose

                    } else if (rest.beforeClose) {
                        return await rest.beforeClose()
                    }

                }
            } : {}
            }
            onClose={async () => {
                await handleClose()
                if (rest.onClose) {
                    await rest.onClose()
                }
            }}
            BackdropComponentProps={{
                ...BackdropComponentProps
            }}
            PaperComponentProps={{
                color: 'paper1',
                size: 'default',
                ...PaperComponentProps,
                ContentComponentProps: {
                    title: dialogProps.dialogTitle ?
                        <Typography variant={'h2'}>{dialogProps.dialogTitle}</Typography> : null,
                    footerActions: [
                        ...dialogProps.cancelText ? [
                            <Button
                                size={size}
                                onClick={handleCancel}
                                key={'cancel'}
                                label={dialogProps.cancelText}
                                color={'tertiary'}
                                {...dialogProps.CancelProps ? dialogProps.CancelProps : {}}
                            />
                        ] : [],
                        ...dialogProps.submitText ? [
                            <SubmitButtonComponent
                                {...SubmitButtonComponent === DefaultSubmit ? {
                                    ContainerProps: {
                                        className: style.submitButton
                                    }
                                } : {
                                    className: style.submitButton
                                }}
                                size={size}
                                onClick={async (e) => await handleSubmit(e)}
                                key={'submit'}
                                label={dialogProps.submitText}
                                color={'primary'}
                                effect={({ actions }) => {
                                    submitRef.actions = actions
                                }}
                                {...dialogProps.SubmitProps ? dialogProps.SubmitProps : {}}
                            />
                        ] : []
                    ],
                    ...PaperComponentProps.ContentComponentProps ? PaperComponentProps.ContentComponentProps : {}
                }
            }}
        >
            <div
                ref={scrollElement}
                className={clsx(
                    style.dialogContent,
                    dialogProps.dialogContentClassName
                )}>
                <ContentComponent {...ContentComponentProps}>{dialogProps.dialogContent}</ContentComponent>
                {(Form) ?
                    <Form
                        submitRef={submitRef}
                        setFormRef={form}
                        onSubmit={formSubmit}
                    />
                    : null
                }
            </div>
            <Dialog
                effect={({ actions }) => {
                    innerDialog.actions = actions
                }}
                showLeaveWarning={false}
                ContentComponentProps={{
                    Component: 'div'
                }}
            />
        </ModalComponent>
    )
}
